import { format } from "date-fns";
import { Box } from "@mui/material";
import { ar, enUS } from "date-fns/locale";
import { useTranslation } from "react-i18next";

import { HireJobDetails } from "../../api/types/job.types";
import {
  JobEndDateIcon,
  JobModeIcon,
  LocationIcon,
  PlayerPositionIcon,
  ProfessionalJobIcon,
  SalaryIcon,
} from "../../assets/icons/go-hire-page-icons";

import JobOverview from "./JobOverview";
import JobDescription from "./JobDescription";
import SubmissionInfo from "./SubmissionInfo";
import CandidatesLocations from "./CandidatesLocations";
import { HiringMission } from "../../enum/jobEnums";

const JobInfo = ({
  jobDetails,
  isJobDetailsLoading,
}: {
  jobDetails?: HireJobDetails;
  isJobDetailsLoading: boolean;
}) => {
  const { t, i18n } = useTranslation("goHire", { keyPrefix: "jobDetailsPage.overViewTab" });
  const { t: tEnum } = useTranslation("goHire", { keyPrefix: "enum" });
  const { t: tJobDetails } = useTranslation("goHire", { keyPrefix: "createJobPage.jobDetails" });
  const { t: tUserRole } = useTranslation("common", { keyPrefix: "user.role" });

  const selectedLocale = i18n.language === "ar" ? ar : enUS;

  const jobTitle = jobDetails?.name;
  const isTalentJob = jobDetails?.hiring_mission === HiringMission.TALENT_DISCOVERY || !!jobDetails?.primaryPosition;

  const roleInfo = isTalentJob
    ? {
        icon: <PlayerPositionIcon />,
        title: `${jobDetails?.primaryPosition ?? "--"}, ${jobDetails?.secondaryPosition ?? "--"}`,
      }
    : {
        icon: <ProfessionalJobIcon />,
        title: jobDetails?.professional_role ? tUserRole(jobDetails.professional_role) : "--",
      };

  const jobMode = {
    icon: <JobModeIcon />,
    title: jobDetails?.jobMode ? tEnum(`jobMode.${jobDetails?.jobMode}`) : "--",
  };

  const jobLocation = {
    icon: <LocationIcon />,
    title:
      jobDetails?.jobLocationCountries && jobDetails.jobLocationCountries.length > 0
        ? jobDetails.jobLocationCountries.map((c) => c.name).join(", ")
        : "--",
  };

  const jobSalary = {
    icon: <SalaryIcon />,
    title: t("salary", {
      currency: jobDetails?.compensation.currency,
      salary: (jobDetails?.compensation.amount ?? 0) / 100,
      per: jobDetails?.compensation.period ? tEnum(`salaryPer.${jobDetails?.compensation.period}`) : "",
    }),
  };

  const jobEndDate = {
    icon: <JobEndDateIcon />,
    title: jobDetails?.applyEndsAt
      ? t("endIn", {
          date: format(new Date(jobDetails?.applyEndsAt), "dd MMM yyyy", { locale: selectedLocale }),
        })
      : "--",
  };

  const overviewList = [roleInfo, jobMode, jobLocation, jobSalary, jobEndDate];

  const whoCanApplyList: { title: string }[] = [];
  if (jobDetails?.advancedTargeting.verificationRule === "VERIFIED_ONLY") {
    whoCanApplyList.push({ title: tEnum("applyCriteria.verified") });
  }
  if (jobDetails?.advancedTargeting.onlyTalentsWithTestimonials) {
    whoCanApplyList.push({ title: tEnum("applyCriteria.onlyTalentsWithTestimonials") });
  }
  if (jobDetails?.advancedTargeting.profileCompletionAbove80) {
    whoCanApplyList.push({ title: tEnum("applyCriteria.profileCompletionAbove80") });
  }

  const idealCandidateInfoList: {
    title: string;
    list?: (string | number)[];
    description?: string | null;
  }[] = [];
  if (jobDetails?.requirements.languagesSpoken && jobDetails?.requirements.preferredLanguages?.length > 0)
    idealCandidateInfoList.push({
      title: tJobDetails("languagesSpoken"),
      list: jobDetails?.requirements.preferredLanguages,
    });
  if (jobDetails?.requirements.preferredSportsEntityIds && jobDetails?.requirements.preferredSportsEntityIds.length > 0)
    idealCandidateInfoList.push({
      title: tJobDetails("specifyClubsAcademiesWorkedIn"),
      list: jobDetails?.requirements.preferredSportsEntities.map((entity) => entity.name),
    });

  if (jobDetails?.requirements.majorInjury)
    idealCandidateInfoList.push({
      title: tJobDetails("anyMajorInjury"),
    });

  if (jobDetails?.requirements.introVideo)
    idealCandidateInfoList.push({
      title: tJobDetails("submitIntroductoryVideo"),
      description: jobDetails?.requirements.videoDescription,
    });

  if (jobDetails?.requirements.willingToRelocate)
    idealCandidateInfoList.push({
      title: tJobDetails("willingnessToRelocate"),
    });

  return (
    <Box sx={styles.sectionContainer} component="section">
      <JobOverview jobTitle={jobTitle} overviewList={overviewList} isOverviewLoading={isJobDetailsLoading} />
      <JobDescription jobDescription={jobDetails?.description} isJobDescriptionLoading={isJobDetailsLoading} />
      <CandidatesLocations
        countries={jobDetails?.advancedTargeting.locationCountries ?? []}
        isCountriesLoading={isJobDetailsLoading}
      />
      <SubmissionInfo
        idealCandidateInfoList={idealCandidateInfoList}
        whoCanApplyList={whoCanApplyList}
        isSubmissionInfoLoading={isJobDetailsLoading}
      />
    </Box>
  );
};

export default JobInfo;

const styles = {
  sectionContainer: {
    backgroundColor: "#FFF",
    borderRadius: "12px",
    border: "1px solid #EAECF0",
    display: "flex",
    flexDirection: "column",
    padding: "32px",
    gap: "24px",
  },
};
